import { get } from 'lodash';

import actions, { COMPONENTS, USER_STATES } from 'state/constants';
import CartSelector from 'state/selectors/cart';
import { getCurrentProduct, getCurrentVariant } from 'state/selectors/pdp';
import Customer from 'utils/Customer';
import Device from 'utils/Device';
import DynamicYield from 'utils/DynamicYield';
import Iterable from 'utils/Iterable';
import Rudderstack from 'utils/Rudderstack';
import Settings from 'utils/Settings';

const signUpForNewsletter = (action, prevState, nextState) => {
  if (action.type === actions.SIGN_UP_NEWSLETTER_REQUEST) {
    const email = get(action, ['payload', 'emailAddress']);
    const phone = get(action, ['payload', 'phone']);
    const acceptsMarketing = get(action, ['payload', 'acceptsMarketing']);

    const origination = get(action, ['meta', 'origination']);
    const purpose = get(action, ['meta', 'purpose']);
    const incentiveDiscount = get(action, ['meta', 'incentiveDiscount']);
    const incentiveType = get(action, ['meta', 'incentiveType']);

    const signupSourceURL = window.location.pathname;

    if (origination === 'productUpdatesForm') {
      const pdpSignupFlow = get(action, ['payload', 'pdpSignupFlow']);
      const currentProduct = getCurrentProduct(nextState);
      const currentVariant = getCurrentVariant(nextState);

      Iterable.push({
        email,
        signupSource: origination,
        pdpSignupFlow,
        locale: Settings.currentLocale,
        siteRegion: Settings.shopID,
        deviceType: Device.getDeviceType(),
        accepts_marketing: true,
        productData: {
          name: get(currentProduct, 'masterName'),
          sku: get(currentVariant, 'sku'),
          categories: {
            allbirdsEdition: get(currentProduct, 'edition'),
            allbirdsGender: get(currentProduct, 'gender'),
            allbirdsHue: get(currentProduct, 'colorName'),
            allbirdsMaster: get(currentProduct, 'masterId'),
            allbirdsMaterial: get(currentProduct, 'material'),
            allbirdsSilhouette: get(currentProduct, 'silhouette'),
          },
        },
      });
      Customer.identifyActionEmail(email, USER_STATES.IDENTIFIED);
      Rudderstack.identifyUser({
        email,
        additionalTraits: {
          marketing_email_subscription: true,
          marketing_email_subscription_source: origination,
        },
      });
      Rudderstack.userMarketingPreference({
        ...Rudderstack.defaultMarketingPreferencesPayload(),
        placement: origination,
        optInStatus: true,
        cmsProductId: get(currentProduct, 'id'),
        sku: get(currentVariant, 'sku'),
      });
    } else if (origination === 'myAccount') {
      DynamicYield.emailSignUpEvent(email);
      Customer.identifyActionEmail(email, USER_STATES.LOGGED_IN);

      if (acceptsMarketing !== null) {
        // Only send opt in events if we have a valid acceptsMarketing value
        Iterable.push({
          email,
          signupSource: origination,
          signupSourceURL,
          locale: Settings.currentLocale,
          siteRegion: Settings.shopID,
          deviceType: Device.getDeviceType(),
          accepts_marketing: acceptsMarketing,
        });

        Rudderstack.userMarketingPreference({
          ...Rudderstack.defaultMarketingPreferencesPayload(),
          placement: origination,
          optInStatus: acceptsMarketing,
        });

        const currentDate = new Date();
        Rudderstack.identifyUser({
          email,
          additionalTraits: {
            marketing_email_subscription: acceptsMarketing || false,
            marketing_email_subscription_source: origination,
            account_sign_up_date: Rudderstack.formatDate(currentDate),
            registered_user: true,
          },
        });
      }
    } else if (
      origination === 'signupModule' ||
      origination === 'signupModal' ||
      origination === 'footer' ||
      origination === 'stayInTouch' ||
      origination === 'homepageSignupModule'
    ) {
      Iterable.push({
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
        signupSource: origination,
        signupSourceURL,
        locale: Settings.currentLocale,
        siteRegion: Settings.shopID,
        deviceType: Device.getDeviceType(),
        accepts_marketing: acceptsMarketing,
        ...(incentiveDiscount ? { incentiveDiscount } : {}),
        ...(incentiveType ? { incentiveType } : {}),
        ...(purpose ? { purpose } : {}),
      });
      Customer.identifyActionEmail(email, USER_STATES.IDENTIFIED);
      Rudderstack.identifyUser({
        email,
        additionalTraits: {
          marketing_email_subscription: acceptsMarketing,
          marketing_email_subscription_source: origination,
        },
      });
      Rudderstack.userMarketingPreference({
        ...Rudderstack.defaultMarketingPreferencesPayload(),
        placement: origination,
        purpose,
        optInStatus: acceptsMarketing,
      });
    }
  }
  return null;
};

const trackCartUpsellMount = (action, prevState, nextState) => {
  const upsell = CartSelector.getCartRecommendation(nextState);

  if (!upsell) {
    return null;
  }

  return {
    hitType: 'event',
    eventAction: 'Is Visible',
    eventCategory: 'Cart',
    eventLabel: 'Upsell',
    eventValue: 1,
    dimension3: get(upsell, ['recommendedColors', 0, 'colorName']),
    dimension7: get(upsell, ['recommendedColors', 0, 'masterName']),
  };
};

const componentShownEvent = (action, prevState, nextState) => {
  const component = get(action, ['meta', 'component']);

  switch (component) {
    case COMPONENTS.CART_UPSELL:
      return trackCartUpsellMount(action, prevState, nextState);
    default:
      return null;
  }
};

const trackNavigation = action => {
  const origination = get(action, ['meta', 'origination']);
  const url = get(action, ['payload', 'url']);

  return {
    hitType: 'event',
    eventAction: origination,
    eventCategory: 'Navigation',
    eventLabel: url,
    eventValue: 1,
  };
};

export { componentShownEvent, trackNavigation, signUpForNewsletter };

export default componentShownEvent;
